import { useNavigate, useParams } from "react-router-dom";
import { FieldErrors, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { confirmDialog } from "primereact/confirmdialog";

import { RewardFormRule, RewardFormSchema } from "@factory/form/form.rule";
import { useFormDefaultReward } from "@factory/form/form-default-reward.hook";
import { useFormApiMapper } from "@factory/services/form-api-mapper.hook";
import { ActionEnum } from "@factory/model/actions.enum";
import { getFormErrorMessage } from "@core/utility/get-error-message";
import {
  useQyUpdateReward,
  useQyGetRewardById,
  useQyDeleteReward,
} from "@factory/query/reward.query";
import { GetResponseRewardModel } from "@factory/model/query-model-reward";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { ButtonAction } from "@shared/ui/header-button/button-action";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";
import { useUserIdentityContext } from "@core/services/user-identity.context";

export function useEditReward() {
  // LOCAL DECLARATION
  const { getUserRole } = useUserIdentityContext();
  const { formatDate } = useDateTimeContext();
  const { getDefault } = useFormDefaultReward();
  const navigate = useNavigate();
  const { mapUpdateReward, mapDeleteReward } = useFormApiMapper();
  const { showError, showSuccess } = useNotificationContext();
  const { rewardId } = useParams();
  const actions: ButtonAction[] = [
    { label: "Update", type: "split", value: ActionEnum.Update },
    { label: "Delete", type: "more", value: ActionEnum.Delete },
  ];

  // QUERY DECLARATION
  // API GETBYID Reward
  const handleGetApiSuccess = (data: GetResponseRewardModel) => {
    if (!data || data.data?.length === 0) {
      return;
    }

    const responseData = data.data?.[0];

    setValue("tenant", responseData?.tenant || "");
    setValue("isPoints", Boolean(responseData?.is_points));
    setValue("isStamp", Boolean(responseData?.is_stamp));
    setValue("denominationPoints", responseData?.denomination_points);
    setValue("points", responseData?.points);
    setValue("denominationStamp", responseData?.denomination_stamp);
    setValue("stamp", responseData?.stamp);
    setValue("isActive", Boolean(responseData?.is_active));
  };
  const {
    data: rewardResponse,
    isLoading,
    isError: rewardError,
  } = useQyGetRewardById(rewardId || "", !!rewardId, handleGetApiSuccess);
  const rewardData = rewardResponse?.data?.[0];

  // API UPDATE Reward
  const handleUpdateApiSuccess = () => {
    showSuccess("Reward updated successfully");
  };
  const { mutate: updateReward, isLoading: isEditLoading } = useQyUpdateReward(
    handleUpdateApiSuccess
  );

  // API DELETE Reward
  const handleDeleteApiSuccess = () => {
    handleBack();
    showSuccess("Reward deleted successfully");
  };
  const { mutate: deleteReward } = useQyDeleteReward(handleDeleteApiSuccess);

  // LOCAL FUNCTIONS
  const formMethod = useForm<RewardFormSchema>({
    defaultValues: getDefault(rewardData),
    resolver: zodResolver(RewardFormRule),
  });
  const { handleSubmit, setValue, watch, getValues } = formMethod;
  const handleValidate = (form: RewardFormSchema) => {
    const { tenant } = getUserRole();
    const updatedForm = {
      ...form,
      tenant,
    };
    if (!rewardData || !rewardData.code) {
      throw new Error("No reward data");
    }

    const formData = mapUpdateReward(updatedForm, rewardData.code);
    updateReward(formData);
  };
  const handleValidateError = (err: FieldErrors<RewardFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };
  const handleBack = () => {
    navigate("../");
  };
  const accept = () => {
    if (!rewardData || !rewardData.code) {
      throw new Error("No reward data found");
    }

    const form = mapDeleteReward(rewardData.code);
    deleteReward(form);
  };
  const handleAction = (action: ButtonAction) => {
    switch (action.value) {
      case ActionEnum.Update:
        handleSubmit(handleValidate, handleValidateError)();
        break;

      case ActionEnum.Delete:
        confirmDialog({
          message: "Are you sure you want to delete this record?",
          header: "Delete Confirmation",
          icon: "pi pi-info-circle",
          defaultFocus: "reject",
          acceptClassName: "p-button-danger",
          accept,
        });
        break;
    }
  };

  return {
    rewardId,
    rewardData,
    isLoading,
    rewardError,
    formMethod,
    actions,
    isEditLoading,
    handleSubmit,
    setValue,
    watch,
    getValues,
    handleBack,
    handleAction,
  };
}
